import { AxiosError } from "axios";
import { t } from "@/plugins/vue-i18n";
import router, { ROUTE_NAME } from "@/router";

import { replaceToast } from "@/functions/baseToast";

import { ErrorHandlerParams } from "@/types/functions";
import {
    AJVErrorBody,
    CustomizedErrorResponse,
    CustomizedResponse,
    ErrorReturnType,
    isAJVErrorBody,
    isAJVErrorObject,
    isErrorReturnType
} from "@/types/functions";

interface CustomizedErrorResponseHandler {
    ajv?: ((error: AJVErrorBody) => any | Promise<any>) | string;
    returnType?: ((error: ErrorReturnType) => any | Promise<any>) | string;
    default?: (() => any | Promise<any>) | string;
}

const undefinedErrorHandler = async (params: ErrorHandlerParams): Promise<void> => {
    let { handler } = params
    const { offset } = params

    if (!handler) handler = 'undefined-response'

    if (typeof handler === 'string') {
        await replaceToast({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            message: t('error.'),
            cssClass: offset ? `toast-offset-${offset}` : undefined,
            id: handler,
            duration: 7500
        }, handler)
        return
    }

    handler(undefined)
}

const ajvErrorHandler = async (params: ErrorHandlerParams<AJVErrorBody>): Promise<void> => {
    const { error, offset } = params
    let { handler } = params

    if (!handler) handler = 'ajv-response'

    if (typeof handler === 'string') {
        let variable: Record<string, string> = {}
        if (error.keyword === 'format') variable = { formatInfo: t(`error.ajv.formatInfo.${error.params?.format}`) }
        if (error.keyword === 'minimum') variable = { min: error.params?.limit }
        if (error.keyword === 'maximum') variable = { max: error.params?.limit }

        await replaceToast({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            message: t(`error.ajv.${error.keyword}`, variable),
            cssClass: offset ? `toast-offset-${offset}` : undefined,
            id: handler,
            duration: 7500
        }, handler)
        return
    }

    handler(error)
}

const errorReturnTypeHandler = async (params: ErrorHandlerParams<ErrorReturnType>): Promise<void> => {
    const { error, offset } = params
    let { handler } = params

    if (!handler) handler = 'error-response'

    if (typeof handler === 'string') {
        await replaceToast({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            message: t(`error.returnType.${error.status}`),
            cssClass: offset ? `toast-offset-${offset}` : undefined,
            id: handler,
            duration: 7500
        }, handler)
        return
    }

    handler(error)
}

const customizedErrorResponseHandler = async (response: CustomizedResponse, handler?: CustomizedErrorResponseHandler, offset?: number): Promise<void> => {
    if (response === undefined) return undefinedErrorHandler({ error: undefined, handler: handler?.default, offset })
    if (isAJVErrorBody(response)) return ajvErrorHandler({ error: response, handler: handler?.ajv, offset })
    if (isErrorReturnType(response)) return errorReturnTypeHandler({
        error: response,
        handler: handler?.returnType,
        offset
    })
}

const isCustomizedErrorResponse = (response: CustomizedResponse): response is CustomizedErrorResponse => {
    if (response === undefined) return true
    if (isAJVErrorBody(response)) return true
    return isErrorReturnType(response)
}

const handleAxiosError = (error: AxiosError, showLog = true): CustomizedErrorResponse => {
    if (error.response?.status === 403 && router.currentRoute.value.name !== ROUTE_NAME.HOME) { 
        router.push({ name: ROUTE_NAME.HOME })  // ticket 6685 → redirect user automatically to location overview
    }

    if (showLog) console.error(error)
    const data = error.response?.data

    if (isErrorReturnType(data)) return data
    if (isAJVErrorObject(data)) return data.body[0]
    return undefined
}

export {
    customizedErrorResponseHandler,
    isCustomizedErrorResponse,
    handleAxiosError
}


